<script setup lang="ts">
import { ref, computed, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { SUPPORTED_LANGUAGES } from "@/config";
import { useStation } from '@/store/station';
import { useUserStore } from "@/store/user";
import { useContactOverlay } from "@/store/overlays/contact-overlay";
import logo_url from '@/assets/logo.png'
import itFlagIcon from '@/assets/flags/it.png'
import frFlagIcon from '@/assets/flags/fr.png'
import enFlagIcon from '@/assets/flags/en.png'
import nlFlagIcon from '@/assets/flags/nl.png'
import deFlagIcon from '@/assets/flags/de.png'
import esFlagIcon from '@/assets/flags/es.png'

const station = useStation();
const user = useUserStore();
const contact_overlay = useContactOverlay();
const { locale } = useI18n({ useScope: 'global' })
const { t } = useI18n();

const logo_ready = ref<boolean>(false);

interface FlagIcons {
  [key: string]: string;
}

const flag_icons = shallowRef<FlagIcons>({
  "it": itFlagIcon,
  "en": enFlagIcon,
  "fr": frFlagIcon,
  "nl": nlFlagIcon,
  "de": deFlagIcon,
  "es": esFlagIcon
});

const selectable_languages = computed(() => {
  return Object.keys(SUPPORTED_LANGUAGES).filter((lang) => lang !== locale.value);
});

const logo = computed(() => `${station.logo}`);

const has_logo = computed(() => {
  const logo = station.logo;
  if (logo) {
    const url = `${logo}`;
    const image: HTMLImageElement = new Image();
    image.onload = () => {
      logo_ready.value = true;
    };
    image.src = url;
  }
  return !!logo;
})

async function logout()
{
  await user.logout();
  await station.do_fetch(station.qrcode, true);
}
</script>

<template>
  <v-app-bar
    class="flat bg-app-bar"
    app elevation="2"
    style="z-index: 2000;"
  >
    <v-expand-transition mode="out-in">
      <img
        v-if="has_logo && logo_ready"
        id="brandLogo"
        key="theresalogo"
        class="ml-4"
        :src="logo"
        contain
      >
      <img
        v-else
        id="RELogo"
        key="theresnologo"
        class="ml-4"
        :src="logo_url"
      >
    </v-expand-transition>

    <v-spacer />

    <v-menu style="z-index: 2001;">
      <template #activator="{ props }">
        <v-btn v-bind="props" icon>
          <img style="max-height: 30px" :src="flag_icons[locale]" :alt="locale"/>
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="language in selectable_languages"
          :key="language"
          @click="locale = language"
        >
          <img style="max-height: 30px" :src="flag_icons[language]" :alt="language"/>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-tooltip location="bottom">
      <template #activator="{ props }">
        <v-btn
          color="contact-icon-color"
          v-bind="props"
          icon
          @click="contact_overlay.is_visible ? contact_overlay.hide() : contact_overlay.show()"
        >
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
      </template>
      <span>{{ t("title") }}</span>
    </v-tooltip>
    <v-tooltip location="bottom">
      <template #activator="{ props }">
        <v-btn color="error" v-bind="props" icon @click="logout">
          <v-icon>mdi-logout</v-icon>
        </v-btn>
      </template>
      <span>{{ t("logout") }}</span>
    </v-tooltip>
  </v-app-bar>
</template>

<i18n lang="yaml">
en:
  title: 'Contact'
  logout: 'Logout'
it:
  title: 'Contatti'
  logout: 'Logout'
de:
  title: 'Contact'
  logout: 'Logout'
fr:
  title: 'Contact'
  logout: 'Logout'
nl:
  title: 'Wie man uns kontaktiert'
  logout: 'Afmelden'
es:
  title: 'Contacto'
  logout: 'Cerrar sesión'
</i18n>

<style scoped lang="scss">
#brandLogo {
  max-height: 2.8em;
  max-width: 13em;
}

#RELogo {
  max-height: 2.5em;
  max-width: 10em;
}
</style>
